import { ReactNode, useEffect, useRef, useState } from "react";

/**
 * Touch-only pull-to-refresh. Wraps the page body. Triggers `onRefresh` when
 * the user pulls down past `threshold` from the top of the scroll container.
 *
 * Why this exists: lots of state in the app is fetched once on mount and then
 * cached client-side. If a user is stuck on a paywall after being manually
 * freed in the backend, or sees a stale value after a webhook hasn't fired
 * yet, they need a way to force a re-fetch. Pull-down is the canonical
 * mobile gesture and works as a universal rescue.
 *
 * Default `onRefresh` is `window.location.reload()` — that re-runs every
 * query the app makes on boot and is safer than trying to hand-pick which
 * caches to bust.
 */
interface Props {
  children: ReactNode;
  onRefresh?: () => void | Promise<void>;
  threshold?: number;
}

const RESIST = 0.5;

export default function PullToRefresh({
  children,
  onRefresh = () => window.location.reload(),
  threshold = 72,
}: Props) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const activeRef = useRef(false);

  useEffect(() => {
    function getScrollTop(): number {
      return (
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      );
    }
    // Elements that declare `touch-action: none` handle their own touch
    // gestures (dnd-kit drag handles on the Übersicht and in der
    // Budget-Verwaltung). A downward drag starting there is a sort, not a
    // pull — without this check, dragging the top widget reloads the page.
    function targetOwnsGesture(target: EventTarget | null): boolean {
      let el: Element | null = target instanceof Element ? target : null;
      for (; el && el !== document.body; el = el.parentElement) {
        if (getComputedStyle(el).touchAction === "none") return true;
      }
      return false;
    }
    function onStart(e: TouchEvent) {
      if (refreshing) return;
      if (getScrollTop() > 0) return;
      const t = e.touches[0];
      if (!t) return;
      if (targetOwnsGesture(e.target)) return;
      startYRef.current = t.clientY;
      activeRef.current = true;
    }
    function onMove(e: TouchEvent) {
      if (!activeRef.current || refreshing) return;
      const t = e.touches[0];
      if (!t || startYRef.current == null) return;
      const dy = t.clientY - startYRef.current;
      if (dy <= 0) {
        if (pull !== 0) setPull(0);
        return;
      }
      // Block the scroll bounce so the gesture feels native (only once we're
      // pulling, so normal upward scroll past the top is unaffected).
      if (e.cancelable) e.preventDefault();
      setPull(dy * RESIST);
    }
    async function onEnd() {
      if (!activeRef.current) return;
      activeRef.current = false;
      const distance = pull;
      startYRef.current = null;
      if (distance >= threshold && !refreshing) {
        setRefreshing(true);
        setPull(threshold);
        try {
          await onRefresh();
        } finally {
          // The default onRefresh reloads the page, so the component
          // unmounts before we reach this. Reset state anyway for callers
          // that pass a custom (non-reloading) handler.
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    }
    document.addEventListener("touchstart", onStart, { passive: true });
    // touchmove must be non-passive so we can preventDefault during a pull.
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [pull, refreshing, threshold, onRefresh]);

  const progress = Math.min(pull / threshold, 1);
  const ready = pull >= threshold;
  const indicatorOpacity = refreshing ? 1 : Math.min(progress, 1);
  const indicatorTranslate = Math.min(pull, threshold + 16);

  return (
    <>
      <div
        aria-hidden={!pull && !refreshing}
        className="fixed top-0 left-0 right-0 z-40 pointer-events-none flex items-center justify-center"
        style={{
          transform: `translateY(${indicatorTranslate - 32}px)`,
          opacity: indicatorOpacity,
          transition: activeRef.current ? "none" : "transform 200ms, opacity 200ms",
        }}
      >
        <div
          className={
            "h-9 w-9 rounded-full bg-ink-900/95 ring-1 ring-ink-700 shadow-md grid place-items-center text-base " +
            (ready || refreshing ? "text-brand-400" : "text-ink-400")
          }
          style={{
            transform: refreshing
              ? "rotate(360deg)"
              : `rotate(${progress * 360}deg)`,
            transition: refreshing ? "transform 800ms linear infinite" : "none",
            animation: refreshing ? "spin 800ms linear infinite" : undefined,
          }}
        >
          {ready || refreshing ? "↻" : "↓"}
        </div>
      </div>
      {/* Content is NOT transformed — applying `transform` to an ancestor
          breaks `position: sticky` for every descendant (the transform
          creates a new containing block). The sub-nav inside AppShell
          needs to stay sticky, so the pull gesture is communicated only
          by the indicator above. */}
      {children}
    </>
  );
}
